<template>
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Cash Register" />
      <div
        class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500">
        <div id="table-header" class="mb-5 flex justify-between items-end hidden">
          <div id="date-filter" class="flex relative">
            <div class="flex flex-col me-3">
              <label for="" class="text-gray-500 dark:text-gray-400 font-medium">Tanggal Mulai</label>
              <input type="date"
                class="mt-1 py-2 w-64 border border-gray-400 rounded-md px-3 text-gray-500 dark:text-gray-400 placeholder:text-gray-400 dark:placeholder:text-gray-500 sm:text-sm sm:leading-6 focus:outline-none focus:ring focus:ring-color-2 dark:bg-dark-800" />
            </div>
            <div class="flex flex-col me-3">
              <label for="" class="text-gray-500 dark:text-gray-400 font-medium">Tanggal Akhir</label>
              <input type="date"
                class="mt-1 py-2 w-64 border border-gray-400 rounded-md px-3 text-gray-500 dark:text-gray-400 placeholder:text-gray-400 dark:placeholder:text-gray-500 sm:text-sm sm:leading-6 focus:outline-none focus:ring focus:ring-color-2 dark:bg-dark-800" />
            </div>
            <div class="flex items-end">
              <SubmitButton button_name="Terapkan"
                button_class="hover:bg-color-2 bg-color-1 text-white border border-color-1" />
            </div>
          </div>
          <div class="w-[18rem]">
            <InputText placeholder="Cari ..." input_class="w-full" />
          </div>
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <div class="flex flex-col gap-2 mb-5 w-2/6">
                <label class="font-medium text-dark-600 dark:text-dark-400">Range Tanggal</label>
                <DatePicker v-model="rangeDate" selectionMode="range" :manualInput="false"
                  @update:modelValue="fetchCashRegisters({ page: 1 })" dateFormat="yy-mm-dd" showIcon class="w-full" />
              </div>
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell v-if="role === 'null'">Outlet</TableHeadCell>
                    <TableHeadCell>Start Time</TableHeadCell>
                    <TableHeadCell>Saldo Awal</TableHeadCell>
                    <TableHeadCell>Total Transaksi Cash</TableHeadCell>
                    <TableHeadCell>Total Withdrawal</TableHeadCell>
                    <TableHeadCell>Total Deposit</TableHeadCell>
                    <TableHeadCell>Saldo Akhir</TableHeadCell>
                    <TableHeadCell>Selisih/Lebih</TableHeadCell>
                    <TableHeadCell>Close Time</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>View</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allCashRegisters.length == 0">
                    <TableCell colspan="12" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(cashRegister, index) in allCashRegisters" :key="cashRegister.id"
                    :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell v-if="role === 'null'">{{this.allOutlets?.find(outlet => outlet.id ==
                      cashRegister.outlet_id)?.name }}</TableCell>
                    <TableCell>{{ formatTime(cashRegister.time_start) }}</TableCell>
                    <!-- <TableCell>{{ cashRegister.start_by }}</TableCell> -->
                    <!-- <TableCell>{{ cashRegister.end_by }}</TableCell> -->
                    <TableCell>{{ formatRupiah(cashRegister.start_saldo) }}</TableCell>
                    <TableCell>{{ formatRupiah(sumClosedTransaction(cashRegister)) }}</TableCell>
                    <TableCell>{{ formatRupiah(withdrawalPettyCash(cashRegister)) }}</TableCell>
                    <TableCell>{{ formatRupiah(depositPettyCash(cashRegister)) }}</TableCell>
                    <TableCell>{{ formatRupiah(cashRegister.end_saldo) }}</TableCell>
                    <TableCell>
                      <span v-if="difference(cashRegister) > 0"
                        class="bg-danger text-white px-2 py-1 rounded-full font-medium">
                        {{ formatRupiah(-difference(cashRegister)) }}
                      </span>
                      <span v-if="difference(cashRegister) < 0"
                        class="bg-success text-white px-2 py-1 rounded-full font-medium">
                        {{ formatRupiah(-difference(cashRegister)) }}
                      </span>
                      <span v-if="difference(cashRegister) == 0"
                        class="bg-warning text-white px-2 py-1 rounded-full font-medium">
                        {{ formatRupiah(difference(cashRegister)) }}
                      </span>
                    </TableCell>
                    <TableCell>{{ formatTime(cashRegister.time_end) }}</TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-success dark:bg-success text-light-50 dark:text-white': cashRegister.status === 'open',
                        'bg-danger dark:bg-danger text-light-50 dark:text-white': cashRegister.status === 'close'
                      }" class="px-2 py-1 rounded-full text-xs font-medium uppercase">
                        {{ cashRegister.status }}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button :id="'view-order-' + cashRegister.id"
                        class="bg-info hover:bg-hover-info flex justify-center items-center mx-1 p-2 rounded-md group"
                        @click="showOrder(cashRegister.id)">
                        <EyeIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
  import IndexView from "@/views/IndexView.vue";
  import PageTitle from "@/components/PageTitle.vue";
  import ViewContainer from "@/components/ViewContainer.vue";
  import {
    TableComponent,
    TableHead,
    TableRow,
    TableHeadCell,
    TableBody,
    TableCell,
  } from "@/components/table";
  import { EyeIcon } from "@heroicons/vue/24/solid";
  import { formatRupiah } from "@/helpers/formatRupiah";
  import SubmitButton from "@/components/form/SubmitButton.vue";
  import InputText from "@/components/form/InputText.vue";
  import { formatTime } from "@/helpers/formatTime";
  import { mapGetters } from "vuex";
  import PaginationButtons from "@/components/PaginationButtons.vue";
import { DatePicker } from "primevue";

  export default {
    name: "ItemView",
    components: {
      IndexView,
      PageTitle,
      ViewContainer,
      TableComponent,
      TableHead,
      TableRow,
      TableHeadCell,
      TableBody,
      TableCell,
      EyeIcon,
      SubmitButton,
      InputText,
      DatePicker,
      PaginationButtons,
    },
    data() {
      return {
        role: localStorage.getItem('role_id'),
        isBlur: false,
        listCashRegister: [],
        showModal: false,
        selectedCashRegisterId: null,
        currentPage: 1,
        totalPages: 1,
        rangeDate: null,
      };
    },
    computed: {
      ...mapGetters('cash_registers', ['allCashRegisters']),
      ...mapGetters('petty_cash', ['allPettyCashes']),
      ...mapGetters('outlets', ['allOutlets']),
    },
    async created() {
      await this.fetchCashRegisters();
      await this.$store.dispatch('petty_cash/fetchPettyCashes');
      await this.$store.dispatch('outlets/fetchOutlets');
    },
    watch: {
      currentPage: {
        immediate: true,
        handler(newVal) {
          this.fetchCashRegisters({ page: newVal });
        },
      },
      rangeDate: {
        immediate: true,
        handler(newVal) {
          if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
          }

          this.searchTimeout = setTimeout(() => {
            if (newVal) {
              const start_date = this.formateISODate(newVal[0]);
              const end_date = this.formateISODate(newVal[1]);
              this.fetchCashRegisters({ start_date, end_date, date_column: 'time_start' });
            } else {
              this.fetchCashRegisters();
            }
          }, 1500);
        },
      },
    },
    methods: {
      formatRupiah,
      formatTime,
      formateISODate(date) {
        const localDate = new Date(date);
        const offset = localDate.getTimezoneOffset();
        const adjustedDate = new Date(localDate.getTime() - (offset * 60 * 1000));
        return adjustedDate.toISOString().split('T')[0];
      },
      async fetchCashRegisters(params = {}) {
        if (!params.page) params.page = this.currentPage;
        if (!params.limit) params.limit = 50;
        try {
          const { pagination } = await this.$store.dispatch('cash_registers/fetchCashRegisters', params);
          if (pagination) {
            this.totalPages = pagination.totalPages;
          }
          console.log(this.role);
        } catch (error) {
          console.log(error);
        }
      },
      showOrder(id) {
        this.$router.push({ name: 'cashRegisterDetail', params: { id: id.toString() } });
      },
      closeModal() {
        this.showModal = false;
        this.selectedCashRegisterId = null;
      },
      sumClosedTransaction(cashRegister) {
        const orders = [...cashRegister.orders];

        // console.log(orders.filter(o => o.status == 'paid')
        // .filter(o => o.payment_method.is_cash_register).map(o => o.grand_total));

        return orders.filter(o => o.status == 'paid')
          .filter(o => o?.payment_method?.is_cash_register)
          .reduce(
            (sum, item) => {
              return sum + parseFloat(item.grand_total);
            },
            0
          )
      },
      withdrawalPettyCash(cashRegister) {
        return this.allPettyCashes.reduce((sum, item) => {
          return sum + (item.cash_register_id === cashRegister.id ? parseFloat(item.amount_withdrawal ? item.amount_withdrawal : 0) : 0);
        }, 0);
      },
      depositPettyCash(cashRegister) {
        return this.allPettyCashes.reduce((sum, item) => {
          return sum + (item.cash_register_id === cashRegister.id ? parseFloat(item.amount_deposit ? item.amount_deposit : 0) : 0);
        }, 0);
      },
      difference(cashRegister) {
        const sum = this.sumClosedTransaction(cashRegister) + this.depositPettyCash(cashRegister) - this.withdrawalPettyCash(cashRegister);

        return parseFloat(cashRegister.start_saldo) + sum - parseFloat(cashRegister.end_saldo == null ? 0 : cashRegister.end_saldo);
      }
    },
  };
</script>